import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
/* eslint-disable ember/no-computed-properties-in-native-classes */
import { action, computed } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { isTesting, macroCondition } from '@embroider/macros';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismButton, PrismIcon, styleEntry } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import type { Dayjs } from 'dayjs';
import { task, timeout } from 'ember-concurrency';
// @ts-expect-error: Incorrect type
import stopPropagation from 'ember-event-helpers/helpers/stop-propagation';
import { t } from 'ember-intl';
import { modifier } from 'ember-modifier';
import { and } from 'ember-truth-helpers';
import type { Hotel, Hotelinfo } from 'frontend-utils/models';
import { OTA_IDS } from 'frontend-utils/utils/constants/constant-values';
import { DOMAIN_NAME } from 'frontend-utils/utils/constants/domain-utils';
import GeneralCountryFlag from 'lumen/components/general/country-flag';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import GeneralLottieAnimation from 'lumen/components/general/lottie-animation';
import GeneralVisibilityBoostVisibilityBoostButton from 'lumen/components/general/visibility-boost/visibility-boost-button';
import HotelPricingAssistantCalendarsDaysTooltipPricingAssistant from 'lumen/components/hotel/pricing-assistant/calendars/days/tooltip-pricing-assistant';
import HotelPricingAssistantRecommendationActionButtonTooltip from 'lumen/components/hotel/pricing-assistant/recommendation-action-button-tooltip';
import HotelPricingAssistantRecommendationActionState from 'lumen/components/hotel/pricing-assistant/recommendation-action-state';
import changeColorClass from 'lumen/helpers/change-color-class';
import hasFeature from 'lumen/helpers/has-feature';
import importAsset from 'lumen/helpers/import-asset';
import type { Carate, Carecommendation, Day, Period } from 'lumen/models';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  compsetDifference,
  compsetDifferenceBackground,
} from 'lumen/utils/compset-difference';
import { MEMBERSHIPTYPES } from 'lumen/utils/constants/general-constants';
import {
  EDIT_RECOMMENDED_PRICE_BUTTON_TYPE,
  type OpenRoomPricingFixedCategory,
  RECOMMENDATION_ACTION_STATES,
  RECOMMENDATIONACTIONS_ACTIONS,
} from 'lumen/utils/constants/pricing-assistant-constants';
import { differenceInDays } from 'lumen/utils/dates/native-js-date-utils';
import findMedianRate from 'lumen/utils/numeric/median';
import {
  findCarateWithLowestAveragePricePriority,
  isCarateForCategory,
  isCarateForRateCode,
  isCarateForSelectedGuest,
} from 'lumen/utils/pricing-assistant/open-pricing-utils';
import { generateRecommendationPushData } from 'lumen/utils/pricing-assistant/usage-analytics-utils';
import { formatUrl } from 'lumen/utils/rates-request';

import styles from './day-pricing-assistant.module.css';

interface SharedMultiViewParams {
  from_date_range_end?: string;
  from_date_range_start?: string;
  hotel_id: string | null;
  'hotelinfo_ids[]'?: string | null;
  los: number;
  mealtype: number;
  membershiptype: number;
  meta: string;
  ota: string;
  persons: number;
  roomtype: string;
  type: string;
}

interface HotelPricingAssistantCalendarsDaysDayPricingAssistantSignature {
  Args: {
    competitorsForCurrentHotelAndOTA: Hotelinfo[] | null;
    currentSelectedMonth: Dayjs;
    dayComponentHeight: string;
    los: number;
    maxPersons: string;
    model: Period;
    redirectToDayDetail: (dayId: string, isEditActive?: boolean) => void;
    selectedRoomCategory: OpenRoomPricingFixedCategory;
  };
}

// Syncs the shimmer animation phase across all loading days. Reads the duration
// from --loading-animation-duration (defined on ::after in app.css) so the CSS
// and JS are never out of sync.
const syncLoadingAnimation = modifier(
  (element: HTMLElement, [active]: [boolean]) => {
    if (active) {
      const durationMs = parseFloat(
        getComputedStyle(element, '::after').getPropertyValue(
          '--loading-animation-duration',
        ),
      );
      element.style.setProperty(
        '--loading-animation-delay',
        `-${Date.now() % durationMs}ms`,
      );
    } else {
      element.style.removeProperty('--loading-animation-delay');
    }
  },
);

export default class HotelPricingAssistantCalendarsDaysDayPricingAssistant extends Component<HotelPricingAssistantCalendarsDaysDayPricingAssistantSignature> {
  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service
  declare pricingAssistantHandleRecommendationActionsService: Services['pricing-assistant-handle-recommendation-actions-service'];
  @service
  declare pricingAssistantRecommendationActionPoller: Services['pricing-assistant-recommendation-action-poller'];
  @service declare usageAnalytics: Services['usage-analytics'];

  sendUsageTrackingEventTask = task({}, async (): Promise<void> => {
    if (macroCondition(!isTesting())) {
      await timeout(3000);
    }
    if (this.differenceOffsetFromToday === null) {
      return;
    }

    this.usageAnalytics.track('Competitors Rates Previewed', {
      eventTrigger: 'calendar-tile-hover',
      product: 'pricing-manager',
      daysOffsetFromToday: this.differenceOffsetFromToday,
    });
  });

  // We don't need to filter the competitor carates based on the selected guest as BE only returns lowest competitor rate for any guest per category
  get competitorCaratesToShow(): Carate[] {
    if (this.access.hasPricingAssistantOpenPricingFeature) {
      return this.model.competitorCarates.filter((carate: Carate) => {
        return isCarateForCategory(carate, this.args.selectedRoomCategory);
      });
    }

    return this.model.competitorCarates;
  }

  get compRateDifference(): number {
    // In case a price is being pushed, we update the calendar day color according to the pushing price
    // Otherwise we update the calendar day color according to the own hotel rate value
    const priceToCalculateCompRateDifference = this
      .showRecommendationPriceBeingPushedOrIsPushed
      ? this.ownHotelRecommendationObject?.priceToShow
      : this.ownHotelPricingAssistantRateObject?.value;

    if (!priceToCalculateCompRateDifference || !this.median) {
      return 0;
    }

    return compsetDifference(priceToCalculateCompRateDifference, this.median);
  }

  get compRateDifferenceBackground(): 'sold-out' | 'green' | 'orange' | 'red' {
    const lowerLimit = this.hotelAndMarketService.hotel.calendarColorLowerLimit;
    const upperLimit = this.hotelAndMarketService.hotel.calendarColorUpperLimit;

    return compsetDifferenceBackground(
      this.compRateDifference,
      lowerLimit,
      upperLimit,
      this.ownHotelPricingAssistantRateObject,
    );
  }

  get compRateDifferenceFormatted(): string {
    return this.ownHotelPricingAssistantRateObject?.value
      ? this.intl.formatNumber(this.compRateDifference, {
          style: 'percent',
          signDisplay: 'always',
        })
      : '';
  }

  get compRatesArray(): number[] {
    const arrCompetitorRates: number[] = [];

    let competitorsAndOwnHotel: Hotelinfo[] = [];
    if (this.args.competitorsForCurrentHotelAndOTA) {
      competitorsAndOwnHotel = [...this.args.competitorsForCurrentHotelAndOTA];
    }

    competitorsAndOwnHotel.forEach((hotel) => {
      const ratesObj = this.competitorCaratesToShow.find((carate: Carate) => {
        return carate.hotelinfo.id === hotel.id;
      });

      if (!ratesObj) {
        return;
      }

      if (ratesObj && ratesObj.value > 0) {
        arrCompetitorRates.push(ratesObj.value);
      }
    });

    return arrCompetitorRates;
  }

  @computed('args.model.day.date')
  get date(): Dayjs {
    return this.args.model.day.date;
  }

  @computed('args.model.day')
  get day(): Day {
    return this.args.model.day;
  }

  get dayInfoBlockBackgroundColorClass():
    | 'day-info-aligned-block--exception'
    | 'sold-out'
    | 'green'
    | 'orange'
    | 'red' {
    // In case of exceptioned recommendation, exception styling takes precedence
    if (
      this.ownHotelRecommendationObject &&
      this.ownHotelRecommendationObject.hasException
    ) {
      return 'day-info-aligned-block--exception';
    }

    return this.compRateDifferenceBackground;
  }

  // Note: all current code here was largely copied over from day-rates for now
  get dayNumber(): number | null {
    if (this.date) {
      return this.date.date();
    }
    return null;
  }

  get differenceOffsetFromToday(): number | null {
    if (!this.date) {
      return null;
    }
    return differenceInDays(
      this.date.toDate(),
      this.hotelAndMarketService.getLocalTime().toDate(),
    );
  }

  get hotel(): Hotel {
    return this.hotelAndMarketService.hotel;
  }

  get isInactiveDay(): boolean {
    return this.isPartOfInactiveMonth || this.isPastDayInActiveMonth;
  }

  get isPartOfInactiveMonth(): boolean {
    if (this.date) {
      return !this.date.isSame(this.args.currentSelectedMonth, 'month');
    }
    return false;
  }

  get isPastDayInActiveMonth(): boolean {
    if (
      this.date &&
      this.date.isSame(this.hotelAndMarketService.getLocalTime(), 'month')
    ) {
      return this.date.isBefore(
        this.hotelAndMarketService.getLocalTime(),
        'day',
      );
    }

    return false;
  }

  get isToday(): boolean {
    if (this.date) {
      return this.date.isSame(this.hotelAndMarketService.getLocalTime(), 'day');
    }
    return false;
  }

  get median(): number | undefined {
    return findMedianRate(this.compRatesArray);
  }

  get model(): Period {
    return this.args.model;
  }

  @cached
  get ownHotelPricingAssistantRateObject(): Carate | null | undefined {
    if (this.access.hasPricingAssistantOpenPricingFeature) {
      return findCarateWithLowestAveragePricePriority(
        this.ownHotelPricingAssistantRateObjectsForSelectedSelections,
      );
    }

    // PriMa Core behaviour:
    // We can assume that there's a carate for every day in the calendar. We can just look this.model.carates
    // Make sure that we display the caRate of the own hotel in the calendar
    return this.model.ownHotelCarates[0];
  }

  get ownHotelPricingAssistantRateObjectsForSelectedSelections(): Carate[] {
    if (this.access.hasPricingAssistantOpenPricingFeature) {
      const filteredCarates = this.model.ownHotelCarates.filter(
        (carate: Carate) =>
          isCarateForCategory(carate, this.args.selectedRoomCategory) &&
          isCarateForSelectedGuest(carate, this.args.maxPersons),
      );

      if (this.access.hasPricingAssistantDynamicRatePricingFeature) {
        const primaStaticRateCode =
          this.hotelAndMarketService.hotel.primaStaticRates[0]?.rate_code || '';
        // For DRP calendar, we only show BAR carates
        return filteredCarates.filter((carate: Carate) =>
          isCarateForRateCode(carate, primaStaticRateCode),
        );
      }

      return filteredCarates;
    }

    return this.model.ownHotelCarates;
  }

  get ownHotelRecommendationObject(): Carecommendation | null {
    if (
      this.ownHotelPricingAssistantRateObject &&
      this.ownHotelPricingAssistantRateObject.carecommendation
    ) {
      return this.ownHotelPricingAssistantRateObject.carecommendation;
    }

    return null;
  }

  get showAutopilotState(): boolean {
    return Boolean(this.ownHotelPricingAssistantRateObject?.autopilotEnabled);
  }

  get showCTAButtons(): boolean {
    return (
      this.access.hasPricingAssistantViewRights && !this.showAutopilotState
    );
  }

  get showEditPriceButton(): boolean {
    return !this.access.hasPricingAssistantOpenPricingFeature;
  }

  get showRecommendation(): boolean {
    return !this.isInactiveDay && this.ownHotelRecommendationObject !== null;
  }

  // Show pushing state in case a recommendation is being pushed
  get showRecommendationBeingPushedState(): boolean {
    return Boolean(this.ownHotelRecommendationObject?.isBeingPushed);
  }

  // Show the price being pushed in case a recommendation is being pushed
  get showRecommendationPriceBeingPushedOrIsPushed(): boolean {
    return Boolean(this.ownHotelRecommendationObject?.isBeingPushedOrIsPushed);
  }

  get showSetPriceButton(): boolean {
    return this.showCTAButtons && this.showEditPriceButton;
  }

  get showSuccessfulPushAnimation(): boolean {
    const recommendationId = this.ownHotelRecommendationObject?.id;

    if (!recommendationId) {
      return false;
    }

    return this.pricingAssistantRecommendationActionPoller.recommendationsWithPushAnimation.has(
      recommendationId,
    );
  }

  showDebugInfo(): void {
    if (!this.access.isStaffUser || !console) {
      return;
    }

    if (!this.ownHotelPricingAssistantRateObject) {
      console.warn('No own hotel rate object found.');
      return;
    }

    const strConsoleLayout = 'color: #005EA0; font-weight: bold;';

    const formattedDate = this.day.date.format('ddd l');
    console.group(`Debugging info: PriMa calendar day - ${formattedDate}`);

    const hotelInfoId = this.hotel.hotelinfo.id;
    const hotelId = this.hotel.id;
    const hotelInfoURL = `${this.ajax.host}/admin/hotel_industry/hotelinfo/${hotelInfoId}/change/`;
    console.log(`%cHotelInfo: ${hotelInfoURL}`, strConsoleLayout);

    const sharedMultiViewParams: SharedMultiViewParams = {
      ota: this.ownHotelPricingAssistantRateObject.ota || OTA_IDS.BOOKINGDOTCOM,
      los: this.ownHotelPricingAssistantRateObject.los,
      persons: this.ownHotelPricingAssistantRateObject.maxPersons,
      mealtype: this.ownHotelPricingAssistantRateObject.mealtypeIncluded,
      membershiptype: MEMBERSHIPTYPES.PUBLIC,
      roomtype: this.ownHotelPricingAssistantRateObject.roomType,
      hotel_id: hotelId,
      meta: 'nested',
      type: 'rates',
    };

    const metasearchHotelsMap = [...(this.model.carates || [])].reduce(
      (acc, rate) => {
        const { hotelinfo, period } = rate;

        const fromDate = period.id?.split(',')[0];

        sharedMultiViewParams['hotelinfo_ids[]'] = hotelinfo.id;
        sharedMultiViewParams.from_date_range_start = fromDate;
        sharedMultiViewParams.from_date_range_end = fromDate;

        const multiViewURL = encodeURI(
          formatUrl(
            `https://viewer.${DOMAIN_NAME}/multiview`,
            sharedMultiViewParams,
          ),
        );

        acc.set(hotelinfo.name, {
          multiViewURL,
          showNonPreferredRateWarningIcon: rate.showNonPreferredRateWarning,
        });

        return acc;
      },
      new Map(),
    );

    metasearchHotelsMap.forEach((value, key) => {
      console.groupCollapsed(key);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      console.log('%cMultiview:', strConsoleLayout, value.multiViewURL);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (value.showNonPreferredRateWarningIcon) {
        console.log(
          '⚠️ This rate does not match with the filters defined for the calendar.',
        );
      }
      console.groupEnd();
    });

    if (this.showRecommendation) {
      console.log(
        'HotelRecommendationObject:',
        this.ownHotelRecommendationObject,
      );
    }
    console.groupEnd();
  }

  @action
  handleEditPriceButtonClicked(): void {
    this.usageAnalytics.track('Price Editing', {
      eventTrigger: 'calendar-tile',
      product: 'pricing-manager',
      daysOffsetFromToday: this.differenceOffsetFromToday,
    });

    if (this.day.id) {
      this.args.redirectToDayDetail(this.day.id, true);
    }
  }

  @action
  onCalendarDayClicked(event: MouseEvent): void {
    // Show debug info on Cmd ⌘ + click
    if (event.metaKey) {
      this.showDebugInfo();
    }

    if (this.isInactiveDay) {
      return;
    }

    if (this.day.id) {
      this.args.redirectToDayDetail(this.day.id);
    }
  }

  @action
  onRecommendationSendButtonClicked(): void {
    if (this.access.hasPricingAssistantOpenPricingFeature) {
      // In case of Open Pricing, we need to push all rates for the selected room category
      const carecommendationAndCaratePairs =
        this.ownHotelPricingAssistantRateObjectsForSelectedSelections
          .filter((carate: Carate) => carate.carecommendation)
          .map((carate: Carate) => ({
            carecommendation: carate.carecommendation,
            carate,
          }));

      void this.pricingAssistantHandleRecommendationActionsService.handleCreateRecommendationActionBulkPush(
        carecommendationAndCaratePairs,
        RECOMMENDATION_ACTION_STATES.ACCEPTED,
        {},
      );
    } else {
      if (this.ownHotelPricingAssistantRateObject) {
        const pricePushParams = generateRecommendationPushData(
          this.ownHotelRecommendationObject,
          this.ownHotelPricingAssistantRateObject,
        );
        this.usageAnalytics.track('Price Recommendation Pushed Manually', {
          eventTrigger: 'calendar-tile',
          product: 'pricing-manager',
          ...pricePushParams,
          daysOffsetFromToday: this.differenceOffsetFromToday,
        });
      }

      if (this.ownHotelRecommendationObject) {
        void this.pricingAssistantHandleRecommendationActionsService.handleCreateRecommendationAction(
          this.ownHotelRecommendationObject,
          RECOMMENDATION_ACTION_STATES.ACCEPTED,
        );
      }
    }
  }

  @action
  onTooltipHide(): void {
    void this.sendUsageTrackingEventTask.cancelAll();
  }

  @action
  onTooltipShow(): void {
    void this.sendUsageTrackingEventTask.perform();
  }

  <template>
    {{! template-lint-disable no-invalid-interactive }}
    {{#if this.ownHotelPricingAssistantRateObject}}
      <div
        class="ins-calendar-day-container
          {{unless this.isInactiveDay 'detail-enabled'}}"
      >
        <div
          class="ins-calendar-day ins-calendar-day--pricing-assistant
            {{unless this.isInactiveDay 'ic-product-tour-day-pricing-manager'}}"
          style={{styleEntry "height" (concat @dayComponentHeight "px")}}
          {{on "click" this.onCalendarDayClicked}}
        >

          <div
            class="calendar-day-content flex flex-col
              {{if this.isInactiveDay 'inactive'}}"
            data-test-id="calendar-day-{{if
              this.isInactiveDay
              'inactive'
              'active'
            }}"
          >
            <div
              class="mb-100 flex flex-row items-center justify-between gap-100"
            >
              <div class="ins-day-number {{if this.isToday 'today'}}">
                {{this.dayNumber}}
              </div>

              <div class="calendar-day-events-section">
                {{! VISIBILITY BOOST PHASE 1: added gap-100 in parent and items center to adjust styles for the boost visibility button, gap-100 in parent container is needed so the event icon does not stick to the day number and items center to center the event icon and country flags vertically }}
                <div class="flex flex-row items-center gap-100">
                  {{#if (hasFeature "app.events")}}
                    {{#if this.day.events.length}}
                      <PrismIcon @name="calendar-accepted" @size="small" />
                    {{/if}}
                    {{#each this.day.holidayCountries as |country|}}
                      <GeneralCountryFlag @countryCode={{country}} />
                    {{/each}}
                  {{/if}}
                  <GeneralVisibilityBoostVisibilityBoostButton
                    @day={{this.day}}
                    @isInactiveDay={{this.isInactiveDay}}
                  />
                </div>
              </div>
            </div>

            <div class="day-info-block-wrapper">
              <div
                class="day-info-aligned-block
                  {{this.dayInfoBlockBackgroundColorClass}}
                  {{if
                    this.showSetPriceButton
                    'day-info-aligned-block--with-hover'
                  }}
                  {{if
                    this.showRecommendationBeingPushedState
                    'day-info-aligned-block--loading'
                  }}"
                data-test-id="day-info"
                {{syncLoadingAnimation this.showRecommendationBeingPushedState}}
              >
                <div class="flex flex-col">
                  {{! Own rate container }}
                  <div
                    class="own-rate-container flex flex-col gap-100"
                    data-test-id="own-rate"
                  >
                    <div class="flex items-center gap-100">
                      {{#if this.ownHotelRecommendationObject.hasException}}
                        <GeneralIconWithTooltip
                          @color="critical"
                          @iconName="warning"
                          @side="top"
                          @size="medium"
                          @tooltipContent={{t
                            "pricing_assistant.exception.action_state.title"
                          }}
                        />
                      {{/if}}
                      {{! Show the pushed price when recommendation is pushed}}
                      {{#if this.ownHotelRecommendationObject.isPushed}}
                        <p
                        >{{this.ownHotelRecommendationObject.formattedPriceToShow}}</p>
                      {{else}}
                        {{#unless this.showRecommendationBeingPushedState}}
                          <p>
                            {{this.ownHotelPricingAssistantRateObject.formattedRate}}
                            {{#if
                              this.ownHotelPricingAssistantRateObject.hasValueAndMessage
                            }}
                              ({{this.ownHotelPricingAssistantRateObject.formattedMessage}})
                            {{/if}}
                          </p>
                          {{#if this.showAutopilotState}}
                            <PrismIcon
                              @color="inherit"
                              @name="automation"
                              @size="small"
                              data-test-id="autopilot-icon"
                            />
                          {{/if}}
                        {{/unless}}
                      {{/if}}
                    </div>
                  </div>
                  {{! Extra info container }}
                  <div
                    class="extra-info-container flex flex-col items-center"
                    data-test-id="extra-info"
                  >
                    {{#if this.showRecommendation}}
                      {{#if
                        this.ownHotelRecommendationObject.showRecommendationActionState
                      }}
                        {{#if this.ownHotelRecommendationObject.isPushed}}
                          {{#if this.showSuccessfulPushAnimation}}
                            {{! @glint-nocheck: not typesafe yet }}
                            <GeneralLottieAnimation
                              @loop={{false}}
                              @path={{importAsset
                                "animations/pricing-assistant-successful-push-animation.json"
                              }}
                              class="successful-push-animation-container"
                            />
                          {{/if}}
                        {{else}}
                          {{! Recommendation in action state except in case of exception (visualised as normal day with specific exception styling) }}
                          <HotelPricingAssistantRecommendationActionState
                            @hasSmallText={{true}}
                            @recommendationObject={{this.ownHotelRecommendationObject}}
                          />
                        {{/if}}
                      {{else}}
                        {{! Recommendation bar container }}
                        <div
                          class="recommendation-bar-container flex flex-row items-center justify-between"
                          data-test-id="recommendation-bar"
                        >
                          {{#if
                            (and
                              this.ownHotelRecommendationObject.hasException
                              this.ownHotelRecommendationObject.isRecommendedPriceOverwritten
                            )
                          }}
                            {{! Recommendation day in exception state with an overwritten value }}
                            <p class="text-200--regular">
                              <span
                                class="strikethrough-text"
                              >{{this.ownHotelRecommendationObject.formattedRecommendedValue}}</span>
                              <span
                                class="text-200--bold"
                              >{{this.ownHotelRecommendationObject.formattedOverwrittenValue}}</span>
                            </p>
                          {{else}}
                            {{! Unactioned recommendation day or a day in exception state without an overwritten value }}
                            <div class="flex items-center">
                              {{#if
                                this.ownHotelRecommendationObject.formattedDifferenceBetweenRecommendationAndOwnRate
                              }}
                                <span
                                  class={{changeColorClass
                                    this.ownHotelRecommendationObject.formattedDifferenceBetweenRecommendationAndOwnRate.difference
                                  }}
                                >
                                  <PrismIcon
                                    @color="inherit"
                                    @name={{if
                                      this.ownHotelRecommendationObject.formattedDifferenceBetweenRecommendationAndOwnRate.isPositiveDifference
                                      "arrow-up"
                                      "arrow-down"
                                    }}
                                    @size="small"
                                  />
                                </span>
                              {{/if}}
                              <p class="text-200--regular">
                                <span
                                  class="text-200--bold"
                                >{{this.ownHotelRecommendationObject.formattedRecommendedValue}}</span>
                                {{#if
                                  this.ownHotelRecommendationObject.formattedDifferenceBetweenRecommendationAndOwnRate
                                }}
                                  <span
                                    class="{{changeColorClass
                                        this.ownHotelRecommendationObject.formattedDifferenceBetweenRecommendationAndOwnRate.difference
                                      }}
                                      text-200--regular recommendation-difference-formatted"
                                  >{{this.ownHotelRecommendationObject.formattedDifferenceBetweenRecommendationAndOwnRate.formattedDifference}}</span>
                                {{/if}}
                              </p>
                            </div>
                          {{/if}}

                          {{#if this.showCTAButtons}}
                            <div class="flex flex-row gap-100">
                              {{#if this.showEditPriceButton}}
                                <div class="flex items-center">
                                  <PrismButton
                                    @buttonType="secondary"
                                    @icon="edit-pencil"
                                    @isDisabled={{this.access.isPricingAssistantActionOrInputDisabled}}
                                    @onClick={{stopPropagation
                                      this.handleEditPriceButtonClicked
                                    }}
                                    @size="small"
                                    data-test-id="day-pricing-assistant-recommendation-bar-edit-button"
                                  />
                                  <HotelPricingAssistantRecommendationActionButtonTooltip
                                    @buttonType={{EDIT_RECOMMENDED_PRICE_BUTTON_TYPE}}
                                  />
                                </div>
                              {{/if}}
                              <div class="flex items-center">
                                <PrismButton
                                  @icon="send"
                                  @isDisabled={{this.access.isPricingAssistantActionOrInputDisabled}}
                                  @onClick={{stopPropagation
                                    this.onRecommendationSendButtonClicked
                                  }}
                                  @size="small"
                                />
                                <HotelPricingAssistantRecommendationActionButtonTooltip
                                  @buttonType={{RECOMMENDATIONACTIONS_ACTIONS.ACCEPTED}}
                                />
                              </div>
                            </div>
                          {{/if}}
                        </div>
                      {{/if}}
                    {{else}}
                      {{#unless this.isInactiveDay}}
                        {{#if this.showSetPriceButton}}
                          <div class="set-price-button-wrapper">
                            <PrismButton
                              @buttonType="secondary"
                              @iconBefore="edit-pencil"
                              @isDisabled={{this.access.isPricingAssistantActionOrInputDisabled}}
                              @label={{t
                                "pricing_assistant.day_tile.no_recommendation.set_price"
                              }}
                              @onClick={{stopPropagation
                                this.handleEditPriceButtonClicked
                              }}
                              @size="small"
                              data-test-id="set-price-button"
                            />
                            <HotelPricingAssistantRecommendationActionButtonTooltip
                              @buttonType={{EDIT_RECOMMENDED_PRICE_BUTTON_TYPE}}
                              @showTooltipIfActionButtonsEnabled={{false}}
                            />
                          </div>
                        {{/if}}
                      {{/unless}}

                      {{#if this.ownHotelPricingAssistantRateObject.hasValue}}
                        {{! Compset percentage container }}
                        <div
                          class="comp-percentage-container flex items-center justify-center"
                          data-test-id="comp-percentage"
                        >
                          <p
                            class="text-200--medium"
                          >{{this.compRateDifferenceFormatted}}
                            {{t "rates.vscomp"}}</p>
                        </div>
                      {{/if}}
                    {{/if}}
                  </div>
                </div>
              </div>
              <EmberTooltip
                @onHide={{this.onTooltipHide}}
                @onShow={{this.onTooltipShow}}
                @side="right"
                @tooltipClass="base-tooltip {{styles.tooltip}}"
              >
                <HotelPricingAssistantCalendarsDaysTooltipPricingAssistant
                  @competitorCaratesToShow={{this.competitorCaratesToShow}}
                  @date={{this.date}}
                  @los={{@los}}
                  @maxPersons={{@maxPersons}}
                  @ownHotelPricingAssistantRate={{this.ownHotelPricingAssistantRateObject}}
                  @ownHotelRecommendation={{this.ownHotelRecommendationObject}}
                  @period={{this.model}}
                  @selectedRoomCategory={{@selectedRoomCategory}}
                  @showAutopilotState={{this.showAutopilotState}}
                  @showRecommendation={{this.showRecommendation}}
                />
              </EmberTooltip>
            </div>
          </div>
        </div>
      </div>
    {{else}}
      {{! For open pricing it can happen that we don't have a carate for a certain selection. In that case we render the day without the carate }}
      <div class="ins-calendar-day-container">
        <div
          class="ins-calendar-day ins-calendar-day--pricing-assistant
            {{unless this.isInactiveDay 'ic-product-tour-day-pricing-manager'}}"
          style={{styleEntry "height" (concat @dayComponentHeight "px")}}
        >

          <div
            class="calendar-day-content flex flex-col
              {{if this.isInactiveDay 'inactive'}}"
          >
            <div class="mb-100 flex flex-row justify-between">
              <div class="ins-day-number {{if this.isToday 'today'}}">
                {{this.dayNumber}}
              </div>

              <div class="calendar-day-events-section">
                <div class="flex flex-row gap-100">
                  {{#if (hasFeature "app.events")}}
                    {{#if this.day.events.length}}
                      <PrismIcon @name="calendar-accepted" @size="small" />
                    {{/if}}
                    {{#each this.day.holidayCountries as |country|}}
                      <GeneralCountryFlag @countryCode={{country}} />
                    {{/each}}
                  {{/if}}
                </div>
              </div>
            </div>

            <div class="day-info-block-wrapper">
              <div class="day-info-aligned-block sold-out">
                <div class="flex flex-col">
                  <div class="own-rate-container flex flex-col"></div>
                  <div class="extra-info-container flex flex-col"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    {{/if}}
  </template>
}
